import React, {FC, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ErrorMessage} from '@hookform/error-message';
import {useUpdateEffect} from 'hooks';
import {
  useAddCompanyToStationMutation,
  useLazyGetUnassignedCompaniesToAddToStationQuery,
} from 'store/slices/stations';
import {IGetStationsList as IGetStation} from 'store/models/stations';
import {ISnackbarState} from 'store/models/common';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Modal,
  Paper,
  Snackbar,
  TextField,
} from '@mui/material';
import ButtonWithProgress from 'components/ButtonWithProgress/ButtonWithProgress';
import {CloseIcon, SelectPopupIcon, SuccessNotification} from 'assets/icons';
import {modal} from 'styles/MUIStyles/modal';
import {searchAndSelect, searchAndSelectError} from 'styles/MUIStyles/common';
import {cancelButton, createButtonModal} from 'styles/MUIStyles/buttons';
import {
  snackbarAlertContent,
  snackbarSuccessAlert,
} from 'styles/MUIStyles/snackbars';
import styles from './StationsAddCompany.module.scss';
import {getServerErrors} from 'core/utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clickedStation: IGetStation | null;
  isStopPropogation?: boolean;
}

interface IFormData {
  company: {
    id: string;
    name: string;
  };
}

const StationsAddCompany: FC<Props> = (props) => {
  const {
    handleSubmit,
    control,
    setError,
    reset,
    clearErrors,
    formState: {errors},
  } = useForm<IFormData>({
    defaultValues: {
      company: {
        id: '',
        name: '',
      },
    },
  });

  const [
    getUnassignedCompanies,
    {
      data: unAssignedCompanies,
      isFetching: isFetchingUnassignedCompaniesLoading,
    },
  ] = useLazyGetUnassignedCompaniesToAddToStationQuery();

  const [
    addCompanyToStation,
    {
      isLoading: isAddingCompanyToStationLoading,
      isSuccess: isAddingCompanyToStationSuccess,
      error: addCompanyToStationError,
    },
  ] = useAddCompanyToStationMutation();

  const [companyName, setCompanyName] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<ISnackbarState>({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const {vertical, horizontal, open} = snackbar;

  const onSubmit = async (data: IFormData) => {
    if (props.clickedStation) {
      const response = await addCompanyToStation({
        stationId: props.clickedStation.id,
        companyId: data.company.id,
      });

      if (response && !('error' in response)) {
        setCompanyName(data.company.name);
      }
    }
  };

  const clearDataAndErrors = () => {
    reset();
    clearErrors();
    props.onClose();
  };

  const handleSnackbarClose = () => {
    setCompanyName(null);
    setSnackbar({...snackbar, open: false});
  };

  useUpdateEffect(() => {
    if (props.isOpen && props.clickedStation) {
      getUnassignedCompanies(props.clickedStation.id);
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (
      addCompanyToStationError &&
      'data' in addCompanyToStationError &&
      'status' in addCompanyToStationError
    ) {
      if (
        addCompanyToStationError.status === 400 ||
        addCompanyToStationError.status === 404
      ) {
        const errors = getServerErrors(addCompanyToStationError.data);

        if (errors?.stationId?.[0]) {
          setError('company', {type: 'custom', message: errors.stationId[0]});
        }

        if (errors?.stationId?.[0]) {
          setError('company', {type: 'custom', message: errors.stationId[0]});
        }
      }
    }
  }, [addCompanyToStationError, setError]);

  useUpdateEffect(() => {
    if (isAddingCompanyToStationSuccess) {
      setSnackbar({vertical: 'top', horizontal: 'center', open: true});
      reset();
      props.onClose();
    }
  }, [isAddingCompanyToStationSuccess]);

  return (
    <>
      <Modal open={props.isOpen} onClose={clearDataAndErrors}>
        <Box sx={{...modal, width: '35%'}}>
          <form
            onSubmit={(event) => {
              if (props.isStopPropogation) {
                event.stopPropagation();
                handleSubmit(onSubmit)(event);
              } else {
                handleSubmit(onSubmit)(event);
              }
            }}
          >
            <div className={styles.modalTop}>
              <h2 className={styles.stationsAddCompanyTitle}>Add company</h2>
              <CloseIcon onClick={clearDataAndErrors} />
            </div>

            <Controller
              name={'company'}
              control={control}
              rules={{
                validate: (value) => {
                  if (!value?.name && !value?.id) {
                    return 'This field cannot be empty';
                  }
                },
              }}
              render={({field}) => (
                <Autocomplete
                  {...field}
                  options={unAssignedCompanies || []}
                  loading={isFetchingUnassignedCompaniesLoading}
                  popupIcon={<SelectPopupIcon />}
                  PaperComponent={(props) => (
                    <Paper
                      sx={{
                        borderRadius: '12px',
                        boxShadow: 'var(--shadow-card)',
                        marginTop: '8px',
                        cursor: 'pointer',
                      }}
                      {...props}
                    />
                  )}
                  sx={{width: '100%'}}
                  ListboxProps={{
                    style: {maxHeight: '200px'},
                    className: 'autoCompleteListBox',
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value.name === '' ? true : option.id === value.id
                  }
                  getOptionLabel={(option) => (option?.name ? option.name : '')}
                  onChange={(_, value) => {
                    if (value) {
                      field.onChange(value);
                    } else {
                      field.onChange(null);
                    }
                  }}
                  renderOption={(props: any, option) => (
                    <span {...props} className={styles.dropDownItem}>
                      {option?.name}
                    </span>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select company name"
                      sx={
                        errors?.company
                          ? searchAndSelectError
                          : {...searchAndSelect, marginBottom: '16px'}
                      }
                    />
                  )}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name={'company'}
              render={({message}) => (
                <span className={styles.inputErrorMessage}>{message}</span>
              )}
            />
            <Divider
              sx={{backgroundColor: 'var(--border)', margin: '18px 0 14px 0'}}
            />
            <div className={styles.buttonsContainer}>
              <Button sx={cancelButton} onClick={clearDataAndErrors}>
                Cancel
              </Button>
              <ButtonWithProgress
                type="submit"
                sx={{...createButtonModal}}
                loading={isAddingCompanyToStationLoading}
                disabled={isAddingCompanyToStationLoading}
              >
                Add company
              </ButtonWithProgress>
            </div>
          </form>
        </Box>
      </Modal>
      {companyName && props.clickedStation ? (
        <Snackbar
          anchorOrigin={{vertical, horizontal}}
          autoHideDuration={2500}
          open={open}
          onClose={handleSnackbarClose}
          key={vertical + horizontal}
        >
          <Alert icon={false} sx={snackbarSuccessAlert}>
            <Box sx={snackbarAlertContent}>
              <SuccessNotification />
              Company {`"${companyName}"`} successfully added to station{' '}
              {`"${props.clickedStation.name}"`}
              <span onClick={handleSnackbarClose}>
                <CloseIcon />
              </span>
            </Box>
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
};

export default StationsAddCompany;
